import TechTicketsPage from "@/pages/TechTicketsPage";
import React, { Suspense, lazy, useState, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AppLayout } from "@/components/layout/AppLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ClientGate } from "@/components/ClientGate";
import { useAppStore } from "@/store/auth";
import { PERM, hasPermission, hasAnyPermission, hasBankPaymentsAccess } from "@/lib/permissions";
import { usePageviewTracker } from "@/hooks/usePageviewTracker";
import { TicketAlerts } from "@/lib/ticketAlerts";
import { CrownAlerts } from "@/lib/crownAlerts";
import "@/styles/globals.css";

const LoginPage = lazy(() => import("@/pages/LoginPage"));
const VerifyOtpPage = lazy(() => import("@/pages/VerifyOtpPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const HubDashboardPage = lazy(() => import("@/pages/hub/HubDashboardPage"));
const ClientsPage = lazy(() => import("@/pages/hub/ClientsPage"));
const RiskPage = lazy(() => import("@/pages/RiskPage"));
const ReportingPage = lazy(() => import("@/pages/ReportingPage"));
const CutoffSettingsPage = lazy(() => import("@/pages/CutoffSettingsPage"));
const TradeBookingPage = lazy(() => import("@/pages/TradeBookingPage"));
const AutoFeesPage = lazy(() => import("@/pages/AutoFeesPage"));
const BankPaymentsPage = lazy(() => import("@/pages/BankPaymentsPage"));
const DevZonePage = lazy(() => import("@/pages/DevZonePage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const AccountsPage = lazy(() => import("@/pages/AccountsPage"));
const ConvertPage = lazy(() => import("@/pages/ConvertPage"));
const LimitOrdersPage = lazy(() => import("@/pages/LimitOrdersPage"));
const AccountDetailPage = lazy(() => import("@/pages/AccountDetailPage"));
const TradesPage = lazy(() => import("@/pages/TradesPage"));
const TradeDetailPage = lazy(() => import("@/pages/TradeDetailPage"));
const TransactionsPage = lazy(() => import("@/pages/TransactionsPage"));
const TransactionDetailPage = lazy(() => import("@/pages/TransactionDetailPage"));
const BeneficiariesPage = lazy(() => import("@/pages/BeneficiariesPage"));
const BeneficiaryDetailPage = lazy(() => import("@/pages/BeneficiaryDetailPage"));
const ThirdPartiesPage = lazy(() => import("@/pages/ThirdPartiesPage"));
const ThirdPartyDetailPage = lazy(() => import("@/pages/ThirdPartyDetailPage"));
const CommissionsPage = lazy(() => import("@/pages/CommissionsPage"));
const MessagesPage = lazy(() => import("@/pages/MessagesPage"));
const NotificationsCenterPage = lazy(() => import("@/pages/NotificationsCenterPage"));
const StatementsPage = lazy(() => import("@/pages/StatementsPage"));
const ChartsPage = lazy(() => import("@/pages/ChartsPage"));
const ArticlesPage = lazy(() => import("@/pages/ArticlesPage"));
const EconomicCalendarPage = lazy(() => import("@/pages/EconomicCalendarPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const PermissionsAdminPage = lazy(() => import("@/pages/PermissionsAdminPage"));
const NotificationsAdminPage = lazy(() => import("@/pages/NotificationsAdminPage"));
const ScheduledEmailsPage = lazy(() => import("@/pages/ScheduledEmailsPage"));
const ClientFacingAdminPage = lazy(() => import("@/pages/ClientFacingAdminPage"));
const ClientDocumentsAdminPage = lazy(() => import("@/pages/ClientDocumentsAdminPage"));
const LendingPage = lazy(() => import("@/pages/LendingPage"));
const LeadsPage = lazy(() => import("@/pages/LeadsPage"));
const SuperAdminPage = lazy(() => import("@/pages/SuperAdminPage"));
const ApiUsagePage = lazy(() => import("@/pages/ApiUsagePage"));
const ApiUsageErrorsPage = lazy(() => import("@/pages/ApiUsageErrorsPage"));
const ControlCentrePage = lazy(() => import("@/pages/ControlCentrePage"));
const OperationsToolsPage = lazy(() => import("@/pages/OperationsToolsPage"));
const ComplianceToolsPage = lazy(() => import("@/pages/ComplianceToolsPage"));
const EntityScreeningPage = lazy(() => import("@/pages/EntityScreeningPage"));
const AdjudicationPage = lazy(() => import("@/pages/adjudication/AdjudicationPage"));
const PayNowPage = lazy(() => import("@/pages/PayNowPage"));
const DisplayPage = lazy(() => import("@/pages/display/DisplayPage"));
const DisplayScreensPage = lazy(() => import("@/pages/display/DisplayScreensPage"));
const KPIsPage = lazy(() => import("@/pages/KPIsPage"));
const ProfitTradesPage = lazy(() => import("@/pages/kpis/ProfitTradesPage"));
const ReferralsPage = lazy(() => import("@/pages/ReferralsPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const InviteClientPage = lazy(() => import("@/pages/InviteClientPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const MassPaymentsUploadPage = lazy(() => import("@/pages/MassPaymentsUploadPage"));
const MassPaymentsWorkbenchPage = lazy(() => import("@/pages/MassPaymentsWorkbenchPage"));
const MassPaymentsFilesPage = lazy(() => import("@/pages/MassPaymentsFilesPage"));
const OrphansPage = lazy(() => import("@/pages/OrphansPage"));
const ReconPage = lazy(() => import("@/pages/ReconPage"));
const ApplyPage = lazy(() => import("@/pages/apply/ApplyPage"));
const ApplicantApp = lazy(() => import("@/pages/applicant/ApplicantApp"));
const CapturePage = lazy(() => import("@/pages/capture/CapturePage"));
const PaymentInstructionsPage = lazy(() => import("@/pages/PaymentInstructionsPage"));
const ApplicationsPage = lazy(() => import("@/pages/applications/ApplicationsPage"));
const SettlementsQueuePage = lazy(() => import("@/pages/settlements/SettlementsQueuePage"));
const ComplianceQueuePage = lazy(() => import("@/pages/compliance-queue/ComplianceQueuePage"));
const SettlementRecordPage = lazy(() => import("@/pages/settlements/SettlementRecordPage"));
const ApplicationDetailPage = lazy(() => import("@/pages/applications/ApplicationDetailPage"));

if (localStorage.getItem("mfx_dark_mode") !== "false") {
  document.documentElement.classList.add("dark");
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 2, refetchOnWindowFocus: true, staleTime: 5_000 } },
});

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-32">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        <span className="text-body-sm text-dark-300">Loading...</span>
      </div>
    </div>
  );
}

const SESSION_KEY = "mfx_session";
const USER_KEY = "mfx_user";
function saveSession(token: string, user: any) { localStorage.setItem(SESSION_KEY, token); localStorage.setItem(USER_KEY, JSON.stringify(user)); }
function clearSession() { localStorage.removeItem(SESSION_KEY); localStorage.removeItem(USER_KEY); }
function getSavedSession() {
  const token = localStorage.getItem(SESSION_KEY);
  const userStr = localStorage.getItem(USER_KEY);
  if (!token || !userStr) return null;
  try { return { token, user: JSON.parse(userStr) }; } catch { return null; }
}

function _isClientRole(role: string | undefined): boolean {
  return role === "client" || role === "client_sub";
}

type AuthStep = "checking" | "login" | "otp" | "register" | "forgot" | "authenticated";

function AuthGate({ children }: { children: (user: any, sessionToken: string) => React.ReactNode }) {
  const [authStep, setAuthStep] = useState<AuthStep>("checking");
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [otpUsername, setOtpUsername] = useState("");
  const [otpPhone, setOtpPhone] = useState("");
  const [otpTotpAvailable, setOtpTotpAvailable] = useState(false);
  const [otpLoginToken, setOtpLoginToken] = useState("");

  useEffect(() => {
    const saved = getSavedSession();
    if (!saved) {
      if (window.location.pathname === "/register" || window.location.search.includes("token=")) {
        setAuthStep("register");
      } else {
        setAuthStep("login");
      }
      return;
    }
    const base = import.meta.env.VITE_API_BASE_URL ?? "";
    fetch(`${base}/api/v1/auth/session`, { headers: { Authorization: `Bearer ${saved.token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then((data) => {
        setSessionToken(saved.token); setCurrentUser(data.user); setAuthStep("authenticated");
        if (_isClientRole(data.user?.role) && data.user?.accountId && data.user.accountId !== "ALL") {
          useAppStore.getState().selectAccount(data.user.accountId, data.user.accountName || "");
        }
        fetch(`${base}/api/v1/auth/preferences`, { headers: { Authorization: `Bearer ${saved.token}` } })
          .then(r => r.ok ? r.json() : null)
          .then(prefs => {
            if (prefs && typeof prefs.dark_mode === "boolean") {
              localStorage.setItem("mfx_dark_mode", String(prefs.dark_mode));
              if (prefs.dark_mode) document.documentElement.classList.add("dark");
              else document.documentElement.classList.remove("dark");
            }
          }).catch(() => {});
      })
      .catch(() => { clearSession(); setAuthStep("login"); });
  }, []);

  const handlePasswordVerified = (username: string, phone: string, totpAvailable: boolean, loginToken: string) => { setOtpUsername(username); setOtpPhone(phone); setOtpTotpAvailable(totpAvailable); setOtpLoginToken(loginToken); setAuthStep("otp"); };

  const handleOtpVerified = (token: string, user: any) => {
    setSessionToken(token); setCurrentUser(user); saveSession(token, user); setAuthStep("authenticated");
    const role = user?.role;
    if (_isClientRole(role) && user?.accountId && user.accountId !== "ALL") {
      useAppStore.getState().selectAccount(user.accountId, user.accountName || "");
    } else {
      // Internal user — clear any stale account selection so they see the selector
      useAppStore.getState().selectAccount("", "");
    }
    const base = import.meta.env.VITE_API_BASE_URL ?? "";
    fetch(`${base}/api/v1/auth/preferences`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(prefs => {
        if (prefs && typeof prefs.dark_mode === "boolean") {
          localStorage.setItem("mfx_dark_mode", String(prefs.dark_mode));
          if (prefs.dark_mode) document.documentElement.classList.add("dark");
          else document.documentElement.classList.remove("dark");
        }
      }).catch(() => {});
  };

  const handleLogout = async () => {
    if (sessionToken) {
      const base = import.meta.env.VITE_API_BASE_URL ?? "";
      try { await fetch(`${base}/api/v1/auth/logout`, { method: "POST", headers: { Authorization: `Bearer ${sessionToken}` } }); } catch {}
    }
    clearSession(); setSessionToken(null); setCurrentUser(null);
    useAppStore.getState().selectAccount("", "");
    setAuthStep("login"); queryClient.clear();
  };

  // Keep window globals in sync for components that still read them (NotificationBell etc)
  useEffect(() => {
    (window as any).__mfxLogout = handleLogout;
    (window as any).__mfxUser = currentUser;
    (window as any).__mfxSessionToken = sessionToken;
  }, [currentUser, sessionToken]);

  if (authStep === "checking") return <div className="min-h-screen bg-brand-500 flex items-center justify-center"><div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>;
  if (authStep === "login") return <Suspense fallback={<PageLoader />}><LoginPage onPasswordVerified={handlePasswordVerified} onGoToRegister={() => setAuthStep("register")} onForgotPassword={() => setAuthStep("forgot")} /></Suspense>;
  if (authStep === "otp") return <Suspense fallback={<PageLoader />}><VerifyOtpPage username={otpUsername} maskedPhone={otpPhone} totpAvailable={otpTotpAvailable} loginToken={otpLoginToken} onVerified={handleOtpVerified} onBack={() => setAuthStep("login")} /></Suspense>;
  if (authStep === "register") return <Suspense fallback={<PageLoader />}><RegisterPage onRegistered={() => setAuthStep("login")} onGoToLogin={() => { window.history.replaceState({}, "", "/"); setAuthStep("login"); }} /></Suspense>;
  if (authStep === "forgot") return <Suspense fallback={<PageLoader />}><ForgotPasswordPage onBack={() => setAuthStep("login")} onReset={() => setAuthStep("login")} /></Suspense>;

  // Only render children once we have a user — pass user and token directly
  if (!currentUser || !sessionToken) return <PageLoader />;
  return <>{children(currentUser, sessionToken)}</>;
}

function AuthenticatedApp({ user, sessionToken }: { user: any; sessionToken: string }) {
  const accountId = user?.accountId;
  const accountName = user?.accountName;
  const role = user?.role || "client";

  const isAdmin = role === "mfx_admin";
  const isPM = role === "mfx_pm";
  const isCompliance = role === "mfx_compliance";
  const isSettlements = role === "mfx_settlements";
  const isClient = _isClientRole(role);
  const isInternal = isAdmin || isPM || isCompliance || isSettlements;
  const isSuperAdmin = isAdmin && user?.isSuperAdmin === true;

  const selectAccount = useAppStore((s) => s.selectAccount);
  const storeAccountId = useAppStore((s) => s.accountId);

  useEffect(() => {
    if (!isClient || !accountId || accountId === "ALL" || !accountName) return;
    const entitledIds = ((user?.accounts ?? []) as Array<{ accountId: string }>).map((a) => a.accountId);
    if (!storeAccountId) {
      // Initial load — default to the client's primary account.
      selectAccount(accountId, accountName);
    } else if (entitledIds.length && !entitledIds.includes(storeAccountId)) {
      // Persisted/stale selection the client is no longer entitled to — snap
      // back to their primary. (Backend enforces this too; this keeps the UI
      // honest.) A valid switch to another of their companies is preserved.
      selectAccount(accountId, accountName);
    }
  }, [accountId, accountName, isClient, storeAccountId, user]);

  // Internal users land on the MFX Hub. No client is selected until they
  // open one from the Clients page or a widget; the shell renders the
  // staff nav in that state and the client nav once a client is chosen.
  const inHub = isInternal && (!storeAccountId || storeAccountId === "ALL");

  // Clients: show loader while account is being set (should be instant)
  if (isClient && (!storeAccountId || storeAccountId === "ALL")) {
    return <PageLoader />;
  }

  return (
    <BrowserRouter>
      <PageviewTracker />
      {isInternal && <TicketAlerts user={user} />}
      {isInternal && <CrownAlerts user={user} enabled={hasPermission(user, PERM.BP_BALANCES)} />}
      <AppLayout>
        <RoutedErrorBoundary showDetails={isInternal}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={inHub ? <HubDashboardPage /> : <DashboardPage />} />
            {isInternal && <Route path="/clients" element={<ClientsPage />} />}
            <Route path="/accounts" element={<AccountsPage />} />
            <Route path="/convert" element={<ConvertPage />} />
            <Route path="/limit-orders" element={<LimitOrdersPage />} />
            <Route path="/accounts/:ccy" element={<AccountDetailPage />} />
            <Route path="/trades" element={<TradesPage />} />
            <Route path="/trades/:tradeId" element={<TradeDetailPage />} />
            <Route path="/transactions" element={<TransactionsPage />} />
            <Route path="/transactions/:txId" element={<TransactionDetailPage />} />
            <Route path="/beneficiaries" element={<BeneficiariesPage />} />
            <Route path="/beneficiaries/:benefId" element={<BeneficiaryDetailPage />} />
            <Route path="/third-parties" element={<ThirdPartiesPage />} />
            <Route path="/third-parties/:tpId" element={<ThirdPartyDetailPage />} />
            <Route path="/statements" element={<StatementsPage />} />
            <Route path="/lending" element={<ClientGate path="/lending"><LendingPage /></ClientGate>} />
            <Route path="/charts" element={<ChartsPage />} />
            <Route path="/articles" element={<ArticlesPage />} />
            <Route path="/calendar" element={<EconomicCalendarPage />} />
            <Route path="/commissions" element={<CommissionsPage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/notifications" element={<NotificationsCenterPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/referrals" element={<ReferralsPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/mass-payments/upload" element={<MassPaymentsUploadPage />} />
            <Route path="/mass-payments/workbench" element={<MassPaymentsWorkbenchPage />} />
            <Route path="/mass-payments/files" element={<MassPaymentsFilesPage />} />
            <Route path="/mass-payments/orphans" element={<OrphansPage />} />
            {hasPermission(user, PERM.INVITE_CLIENT) && <Route path="/invite" element={<InviteClientPage />} />}
            {isAdmin && <Route path="/admin" element={<AdminPage />} />}
            {isAdmin && <Route path="/admin/permissions" element={<PermissionsAdminPage />} />}
            {isAdmin && <Route path="/admin/scheduled-emails" element={<ScheduledEmailsPage />} />}
            {isSuperAdmin && <Route path="/client-facing" element={<ClientFacingAdminPage />} />}
            {(user?.role === "mfx_admin" || user?.role === "mfx_compliance" || user?.role === "mfx_pm" || isSuperAdmin) && <Route path="/client-documents" element={<ClientDocumentsAdminPage />} />}
            {hasAnyPermission(user, PERM.KPIS_ALL, PERM.KPIS_SCOPED, PERM.KPIS_COMPLIANCE_ALL, PERM.KPIS_COMPLIANCE_OWN) && <Route path="/kpis" element={<KPIsPage />} />}
            {hasAnyPermission(user, PERM.KPIS_ALL, PERM.KPIS_SCOPED) && <Route path="/kpis/profit-trades" element={<ProfitTradesPage />} />}
            {hasAnyPermission(user, PERM.LEADS_ALL, PERM.LEADS_SCOPED) && <Route path="/leads" element={<LeadsPage />} />}
            {isSuperAdmin && <Route path="/super-admin" element={<SuperAdminPage />} />}
            {(isInternal || isSuperAdmin) && <Route path="/tech-tickets" element={<TechTicketsPage />} />}
            {hasPermission(user, PERM.CONTROL_CENTRE) && <Route path="/control-centre" element={<ControlCentrePage />} />}
            {hasPermission(user, PERM.NOTIFICATIONS) && <Route path="/admin/notifications" element={<NotificationsAdminPage />} />}
            {hasPermission(user, PERM.OPS_TOOLS) && <Route path="/operations-tools" element={<OperationsToolsPage />} />}
            {hasPermission(user, PERM.OPS_TOOLS) && <Route path="/operations-tools/recon" element={<ReconPage />} />}
            {hasPermission(user, PERM.COMPLIANCE_TOOLS) && <Route path="/compliance-tools" element={<ComplianceToolsPage />} />}
            {hasPermission(user, PERM.ENTITY_SCREENING) && <Route path="/entity-screening" element={<EntityScreeningPage />} />}
            {hasPermission(user, PERM.ADJUDICATION) && <Route path="/auto-adjudication" element={<AdjudicationPage />} />}
            {hasPermission(user, PERM.PAY_NOW) && <Route path="/pay-now" element={<PayNowPage />} />}
            {hasPermission(user, PERM.DISPLAY_SCREENS) && <Route path="/display/connect" element={<DisplayScreensPage />} />}
            {hasPermission(user, PERM.DISPLAY_SCREENS) && <Route path="/display/screens" element={<DisplayScreensPage />} />}
            {hasPermission(user, PERM.RISK) && <Route path="/risk" element={<RiskPage />} />}
            {hasPermission(user, PERM.REPORTING) && <Route path="/reporting" element={<ReportingPage />} />}
            {(user?.role === "mfx_admin" || user?.isSuperAdmin) && <Route path="/settings/cutoffs" element={<CutoffSettingsPage />} />}
            {isInternal && <Route path="/trade-booking" element={<TradeBookingPage />} />}
            {(isAdmin || isPM || isSettlements || user?.isSuperAdmin === true) && <Route path="/auto-fees" element={<AutoFeesPage />} />}
            {hasBankPaymentsAccess(user) && <Route path="/bank-payments" element={<BankPaymentsPage />} />}
            {hasPermission(user, PERM.SETTLEMENTS_QUEUE) && <Route path="/settlements" element={<SettlementsQueuePage />} />}
            {hasPermission(user, PERM.COMPLIANCE_QUEUE) && <Route path="/compliance-queue" element={<ComplianceQueuePage />} />}
            {hasPermission(user, PERM.SETTLEMENTS_QUEUE) && <Route path="/settlements/record/:recordType/:recordId" element={<SettlementRecordPage />} />}
            {(isAdmin || isPM || isCompliance || isSuperAdmin) && <Route path="/applications" element={<ApplicationsPage />} />}
            {(isAdmin || isPM || isCompliance || isSuperAdmin) && <Route path="/applications/:appId" element={<ApplicationDetailPage />} />}
            {user?.isSuperAdmin && <Route path="/dev" element={<DevZonePage />} />}
            {user?.isSuperAdmin && <Route path="/api-usage" element={<ApiUsagePage />} />}
            {hasPermission(user, PERM.NEW_PAYMENTS) && (
              <Route path="/payments/new" element={<PaymentInstructionsPage />} />
            )}
            {user?.isSuperAdmin && <Route path="/api-usage/errors" element={<ApiUsageErrorsPage />} />}
          </Routes>
        </Suspense>
        </RoutedErrorBoundary>
      </AppLayout>
    </BrowserRouter>
  );
}

// Wraps the routed pages in an error boundary keyed by the current path, so a
// render crash (or a stale lazy-chunk after a deploy) shows a recoverable card
// instead of a blank screen — and navigating to another page clears it.
function RoutedErrorBoundary({ children, showDetails }: { children: React.ReactNode; showDetails: boolean }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname} showDetails={showDetails}>{children}</ErrorBoundary>;
}

// Small component whose only job is to mount the usePageviewTracker
// hook inside the BrowserRouter context (the hook needs useLocation()).
function PageviewTracker() {
  usePageviewTracker();
  return null;
}

// The public application page (/apply) sits outside the login gate.
// Applicants (submitted, no account yet) get their own small shell rather
// than the client or staff app.
const isApplyRoute = window.location.pathname === "/apply" || window.location.pathname.startsWith("/apply/");
// Phone camera capture for proof of identity: opened from a QR code, no
// login (the link's token is the key), so it also sits outside the gate.
const isCaptureRoute = window.location.pathname.startsWith("/capture/");
// The office TV (Fully Kiosk): its own display token, never a login.
const isDisplayRoute = window.location.pathname === "/display" || window.location.pathname === "/display/";

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      {isDisplayRoute ? (
        <Suspense fallback={null}><DisplayPage /></Suspense>
      ) : isCaptureRoute ? (
        <Suspense fallback={<PageLoader />}><CapturePage /></Suspense>
      ) : isApplyRoute ? (
        <Suspense fallback={<PageLoader />}><ApplyPage /></Suspense>
      ) : (
        <AuthGate>
          {(user, sessionToken) => user?.role === "applicant"
            ? <Suspense fallback={<PageLoader />}><ApplicantApp user={user} /></Suspense>
            : <AuthenticatedApp user={user} sessionToken={sessionToken} />}
        </AuthGate>
      )}
    </QueryClientProvider>
  </React.StrictMode>
);
